import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, List, Mail } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export default function LegalDocumentLayout({
  testId,
  title,
  description,
  lastUpdated,
  icon: Icon,
  highlights,
  sections,
  contactTitle,
  contactDescription,
  secondaryLink,
}) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id);
  const [openSections, setOpenSections] = useState(
    () => sections.slice(0, 2).map((section) => section.id)
  );

  useEffect(() => {
    const observers = sections
      .map((section) => {
        const element = document.getElementById(section.id);
        if (!element) return null;

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setActiveSection(section.id);
            }
          },
          { rootMargin: '-15% 0px -65% 0px', threshold: 0 }
        );

        observer.observe(element);
        return { observer, element };
      })
      .filter(Boolean);

    return () => {
      observers.forEach(({ observer, element }) => observer.unobserve(element));
    };
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveSection(id);
    setOpenSections((current) =>
      current.includes(id) ? current : [...current, id]
    );
  };

  const expandAll = () => setOpenSections(sections.map((section) => section.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <div data-testid={testId} className="pt-20">
      <section className="relative overflow-hidden py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-navy via-navy to-slate-800">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,#10b981_0%,transparent_40%),radial-gradient(circle_at_80%_60%,#34d399_0%,transparent_35%)]" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl text-left"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald/20 text-emerald mb-5">
              <Icon className="h-7 w-7" />
            </div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-white mb-4 font-heading">
              {title}
            </h1>
            <p className="text-base leading-relaxed text-slate-300">{description}</p>
            <p className="mt-4 text-sm text-emerald">Last updated: {lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 px-6 md:px-12 lg:px-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {highlights.map((item, index) => (
              <motion.button
                key={item.title}
                type="button"
                onClick={() => scrollToSection(item.targetId)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.06 }}
                whileHover={{ y: -4 }}
                className="group text-left bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-emerald/40 hover:shadow-md transition-all"
              >
                <div className="bg-emerald-light/10 w-11 h-11 rounded-xl flex items-center justify-center mb-3 group-hover:bg-emerald/15 transition-colors">
                  <item.icon className="h-5 w-5 text-emerald" />
                </div>
                <h2 className="font-heading font-semibold text-navy mb-1.5">{item.title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                <span className="mt-3 inline-flex items-center text-xs font-medium text-emerald opacity-0 group-hover:opacity-100 transition-opacity">
                  Jump to section <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="lg:sticky lg:top-28 space-y-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <List className="h-4 w-4 text-emerald" />
                    <h2 className="font-heading font-semibold text-navy text-sm uppercase tracking-wide">
                      On this page
                    </h2>
                  </div>
                  <nav className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                          activeSection === section.id
                            ? 'bg-emerald/10 text-emerald font-medium border-l-2 border-emerald'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-navy'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="hidden lg:flex gap-2">
                  <button
                    type="button"
                    onClick={expandAll}
                    className="flex-1 text-xs font-medium px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:border-emerald/40 hover:text-emerald transition-colors"
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="flex-1 text-xs font-medium px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:border-emerald/40 hover:text-emerald transition-colors"
                  >
                    Collapse all
                  </button>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8 xl:col-span-9">
              <div className="flex flex-wrap gap-2 mb-6 lg:hidden">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      activeSection === section.id
                        ? 'bg-emerald text-white border-emerald'
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}
                  >
                    {section.shortTitle || section.title}
                  </button>
                ))}
              </div>

              <Accordion
                type="multiple"
                value={openSections}
                onValueChange={setOpenSections}
                className="space-y-4"
              >
                {sections.map((section, index) => (
                  <AccordionItem
                    key={section.id}
                    id={section.id}
                    value={section.id}
                    className="border border-slate-200 rounded-2xl bg-white shadow-sm overflow-hidden data-[state=open]:border-emerald/30 data-[state=open]:shadow-md transition-all"
                  >
                    <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-50/80 [&[data-state=open]]:bg-emerald/5">
                      <div className="flex items-start gap-4 text-left">
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald/10 text-emerald text-sm font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <div>
                          <span className="block text-lg font-heading font-semibold text-navy">
                            {section.title.replace(/^\d+\.\s*/, '')}
                          </span>
                          {section.summary && (
                            <span className="block text-sm text-slate-500 mt-1 font-normal">
                              {section.summary}
                            </span>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6 pt-0">
                      <div className="pl-12 border-l-2 border-emerald/20 ml-4">
                        <p className="text-base text-slate-600 leading-relaxed">
                          {section.content}
                        </p>
                        {section.points && (
                          <ul className="mt-4 space-y-2">
                            {section.points.map((point) => (
                              <li
                                key={point}
                                className="flex items-start gap-2 text-sm text-slate-600"
                              >
                                <ChevronRight className="h-4 w-4 text-emerald mt-0.5 flex-shrink-0" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-10 p-6 md:p-8 rounded-2xl bg-navy text-white"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-emerald/20 p-3 rounded-xl flex-shrink-0">
                    <Mail className="h-6 w-6 text-emerald" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-heading font-semibold text-lg mb-2">
                      {contactTitle}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                      {contactDescription}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <Link
                        to="/contact"
                        className="text-emerald hover:text-emerald-light transition-colors font-medium"
                      >
                        Contact us →
                      </Link>
                      {secondaryLink && (
                        <Link
                          to={secondaryLink.to}
                          className="text-slate-300 hover:text-white transition-colors font-medium"
                        >
                          {secondaryLink.label} →
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
